import { useCallback, useEffect, useRef, useState } from 'react';
import { c } from '../theme';

// The TV wall — a screen on the floor that cannot sign in.
//
// First visit: the screen makes its own random secret, keeps it in this
// browser, and sends only the secret's SHA-256 to ask to pair. It shows the
// code it gets back until an administrator enters that code under Contact
// Center → Settings → Displays; then it reads its wall with the secret
// (Authorization: Display ...). The server never sees the secret.
//
// It asks for its wall every 30 seconds, rotates through the views the
// administrator chose, and says plainly when its data is late or the
// connection is gone. If it is unpaired or revoked, it starts pairing again.
// Nothing here uses the portal's sign-in; the wall has no user.

const KEY = 'cc.display.secret';
const POLL_PAIRING_MS = 5_000;
const POLL_WALL_MS = 30_000;
const DWELL_MS = 30_000;

const store = {
  get: () => { try { return localStorage.getItem(KEY); } catch { return null; } },
  set: (v) => { try { localStorage.setItem(KEY, v); } catch { /* private window: pairing lasts this session */ } },
  clear: () => { try { localStorage.removeItem(KEY); } catch { /* nothing to clear */ } },
};

const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
async function newSecret() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const secret = hex(bytes);
  const hash = hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret)));
  return { secret, hash };
}

const fmtCount = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const fmtPct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const fmtWait = (s) => (s == null ? '—' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`);
const slColor = (v) => (v == null ? c.onNavyMuted : v >= 95 ? '#4ADE80' : v >= 90 ? '#FBBF24' : '#F87171');
const effColor = (v) => (v == null ? c.onNavyMuted : v >= 92 ? '#4ADE80' : v >= 87 ? '#FBBF24' : '#F87171');

function Big({ label, value, color, sub }) {
  return (
    <div style={s.big}>
      <div style={s.bigLabel}>{label}</div>
      <div style={{ ...s.bigValue, color: color || c.onNavy }}>{value}</div>
      {sub && <div style={s.bigSub}>{sub}</div>}
    </div>
  );
}

function NowView({ w }) {
  return (
    <div style={s.grid2}>
      <Big label="Calls waiting" value={fmtCount(w.rightNow.waitingPhone)} color={w.rightNow.waitingPhone > 0 ? '#FBBF24' : undefined}
        sub={`Voice, VIP and callbacks · ${fmtCount(w.rightNow.waitingAll)} waiting on every channel`} />
      <Big label="Longest wait" value={fmtWait(w.rightNow.longestWaitSecs)} color={w.rightNow.longestWaitSecs > 60 ? '#F87171' : undefined} />
      <Big label="Agents available" value={fmtCount(w.rightNow.available)} color={w.rightNow.available > 0 ? '#4ADE80' : undefined} />
      <Big label="Service level today" value={fmtPct(w.today.serviceLevel)} color={slColor(w.today.serviceLevel)} sub="Voice + VIP, answered within 60 seconds" />
    </div>
  );
}

function TodayView({ w }) {
  const max = Math.max(1, ...w.hourly.map((h) => h.answered + h.callbacks));
  const label = (h) => `${((h + 11) % 12) + 1}${h < 12 ? 'a' : 'p'}`;
  return (
    <div style={s.today}>
      <div style={s.row}>
        <Big label="Handled today" value={fmtCount(w.today.handled)} sub="each conversation once, every pod" />
        <Big label="Service level today" value={fmtPct(w.today.serviceLevel)} color={slColor(w.today.serviceLevel)} />
        <Big label="Abandoned today" value={fmtCount(w.today.abandons)} sub="Voice + VIP" />
      </div>
      <div style={s.bars} role="img" aria-label="Calls answered by hour">
        {w.hourly.map((h) => (
          <div key={h.hour} style={s.barCol}>
            <div style={s.barSl}>{h.serviceLevel == null ? '' : Math.round(h.serviceLevel)}</div>
            <div style={s.barStack}>
              <div style={{ ...s.bar, height: `${(h.callbacks / max) * 100}%`, background: '#93C5FD' }} />
              <div style={{ ...s.bar, height: `${(h.answered / max) * 100}%`, background: '#E5E7EB' }} />
            </div>
            <div style={s.barLabel}>{label(h.hour)}</div>
          </div>
        ))}
      </div>
      <div style={s.legend}>Bars: Voice + VIP calls answered each hour, callbacks in blue on top. Figures: that hour&rsquo;s service level.</div>
    </div>
  );
}

function PodsView({ w }) {
  return (
    <div style={s.pods}>
      {w.pods.map((p) => (
        <div key={p.pod} style={{ ...s.pod, opacity: p.answered ? 1 : 0.5 }}>
          <div style={s.podName}>{p.pod}</div>
          <div style={s.podBig}>{fmtCount(p.answered)}<span style={s.podUnit}> answered</span></div>
          <div style={s.podRow}>
            <span>SL <b style={{ color: slColor(p.serviceLevel) }}>{fmtPct(p.serviceLevel)}</b></span>
            <span>Eff <b style={{ color: effColor(p.efficiency) }}>{fmtPct(p.efficiency)}</b></span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TopView({ w }) {
  const rows = w.top || [];
  const half = Math.ceil(rows.length / 2);
  const col = (list, offset) => (
    <ol start={offset + 1} style={s.topList}>
      {list.map((a, i) => (
        <li key={`${a.name}-${i}`} style={{ ...s.topRow, color: offset + i === 0 ? '#FCD34D' : c.onNavy }}>
          <span style={s.topName}>{a.name}</span><span style={s.topPod}>{a.pod}</span>
          <span>{fmtCount(a.handled)}</span><span style={{ color: effColor(a.efficiency), minWidth: '70px', textAlign: 'right' }}>{fmtPct(a.efficiency)}</span>
        </li>
      ))}
    </ol>
  );
  return rows.length
    ? <div style={s.grid2}>{col(rows.slice(0, half), 0)}{col(rows.slice(half), half)}</div>
    : <div style={s.pairHint}>No one has handled a call yet today.</div>;
}

const VIEWS = { now: ['Right now', NowView], today: ['Today so far', TodayView], pods: ['Pods', PodsView], top: ['Top performers', TopView] };

function Wall({ wall, lastOk }) {
  const views = (wall.views || []).filter((v) => VIEWS[v]);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (views.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % views.length), DWELL_MS);
    return () => clearInterval(t);
  }, [views.length]);
  const [title, View] = VIEWS[views[i % views.length]] || VIEWS.now;
  const age = Date.now() - lastOk;
  const conn = wall.stale || age > 3 * POLL_WALL_MS
    ? (age > 6 * POLL_WALL_MS ? ['Connection lost', '#F87171'] : ['Data delayed', '#FBBF24'])
    : ['Live', '#4ADE80'];
  const clock = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date());
  return (
    <div style={s.screen}>
      <header style={s.head}>
        <span style={s.brand}>ATHENS SUPPORT CENTER · {title.toUpperCase()}</span>
        <span style={s.clock}>{clock}</span>
        <span style={{ ...s.conn, color: conn[1] }}>● {conn[0]}</span>
      </header>
      <main style={s.main}>{wall.ready ? <View w={wall} /> : <div style={s.pairHint}>{wall.message}</div>}</main>
      <footer style={s.foot}>
        {views.map((v, k) => <span key={v} style={{ ...s.dot, background: k === i % views.length ? c.onNavy : c.onNavyFaint }} />)}
        <span style={s.footLabel}>{wall.label}</span>
      </footer>
    </div>
  );
}

export default function TvWall() {
  const [phase, setPhase] = useState('starting');      // starting | pairing | wall | error
  const [code, setCode] = useState(null);
  const [wall, setWall] = useState(null);
  const [lastOk, setLastOk] = useState(0);
  const [error, setError] = useState(null);
  const secretRef = useRef(store.get());

  const beginPairing = useCallback(async () => {
    try {
      const { secret, hash } = await newSecret();
      const res = await fetch('/api/contact-center/tv/pair/start', {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ tokenHash: hash }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Pairing could not start (${res.status}).`);
      store.set(secret);
      secretRef.current = secret;
      setCode(body.code);
      setPhase('pairing');
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  }, []);

  const poll = useCallback(async () => {
    const secret = secretRef.current;
    if (!secret) return beginPairing();
    try {
      const res = await fetch('/api/contact-center/tv/wall', { headers: { authorization: `Display ${secret}` } });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setWall(body);
        setLastOk(Date.now());
        setPhase('wall');
      } else if (res.status === 401 && body.pending) {
        setCode(body.code);
        setPhase('pairing');
      } else if (res.status === 401) {
        store.clear();
        secretRef.current = null;
        await beginPairing();
      }
    } catch {
      // Network down: keep showing the last wall; the chip turns red with time.
    }
    return undefined;
  }, [beginPairing]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, phase === 'wall' ? POLL_WALL_MS : POLL_PAIRING_MS);
    return () => clearInterval(t);
  }, [poll, phase]);

  if (phase === 'wall' && wall) return <Wall wall={wall} lastOk={lastOk} />;
  return (
    <div style={{ ...s.screen, alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
      <div style={s.brand}>ATHENS CONTACT CENTER · WALL DISPLAY</div>
      {phase === 'pairing' && code && (
        <>
          <div style={s.pairHint}>To show the wall on this screen, enter this code in the portal under<br />Contact Center → Settings → Displays</div>
          <div style={s.code} aria-label="Pairing code">{code.slice(0, 4)} {code.slice(4)}</div>
          <div style={s.bigSub}>The code lasts 15 minutes; a new one appears after that.</div>
        </>
      )}
      {phase === 'starting' && <div style={s.pairHint}>Starting…</div>}
      {phase === 'error' && (
        <>
          <div style={{ ...s.pairHint, color: '#F87171' }}>{error}</div>
          <button type="button" style={s.retry} onClick={() => { setPhase('starting'); beginPairing(); }}>Try again</button>
        </>
      )}
    </div>
  );
}

const s = {
  screen:   { minHeight: '100vh', background: c.navyDeep, color: c.onNavy, display: 'flex', flexDirection: 'column', fontFamily: 'inherit', padding: '2vh 3vw', boxSizing: 'border-box' },
  head:     { display: 'flex', alignItems: 'center', gap: '2vw', fontSize: '1.6vw', paddingBottom: '1.5vh', borderBottom: `1px solid ${c.onNavyFaint}` },
  brand:    { fontWeight: 700, letterSpacing: '0.12em', fontSize: '1.6vw' },
  clock:    { marginLeft: 'auto', color: c.onNavyMuted },
  conn:     { fontWeight: 600 },
  main:     { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '2vh 0' },
  foot:     { display: 'flex', gap: '0.6vw', alignItems: 'center', fontSize: '1.1vw', color: c.onNavyMuted },
  footLabel:{ marginLeft: 'auto' },
  dot:      { width: '0.8vw', height: '0.8vw', borderRadius: '50%' },
  grid2:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3vh 4vw' },
  row:      { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3vw' },
  big:      { display: 'flex', flexDirection: 'column', gap: '0.5vh' },
  bigLabel: { fontSize: '1.5vw', letterSpacing: '0.1em', textTransform: 'uppercase', color: c.onNavyMuted },
  bigValue: { fontSize: '7vw', fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  bigSub:   { fontSize: '1.2vw', color: c.onNavyMuted },
  today:    { display: 'flex', flexDirection: 'column', gap: '3vh' },
  bars:     { display: 'flex', alignItems: 'flex-end', gap: '0.8vw', height: '38vh' },
  barCol:   { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' },
  barSl:    { fontSize: '1.2vw', color: c.onNavyMuted, height: '3vh' },
  barStack: { flex: 1, width: '100%', display: 'flex', flexDirection: 'column-reverse' },
  bar:      { width: '100%' },
  barLabel: { fontSize: '1.1vw', color: c.onNavyMuted, marginTop: '0.5vh' },
  legend:   { fontSize: '1.1vw', color: c.onNavyMuted },
  pods:     { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2vh 2vw' },
  pod:      { border: `1px solid ${c.onNavyFaint}`, borderRadius: '12px', padding: '2vh 1.5vw' },
  podName:  { fontSize: '1.6vw', fontWeight: 700 },
  podBig:   { fontSize: '3.6vw', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  podUnit:  { fontSize: '1.2vw', fontWeight: 400, color: c.onNavyMuted },
  podRow:   { display: 'flex', gap: '2vw', fontSize: '1.4vw', color: c.onNavyMuted },
  topList:  { margin: 0, paddingLeft: '2.5vw', display: 'flex', flexDirection: 'column', gap: '1vh', fontSize: '1.6vw' },
  topRow:   { },
  topName:  { display: 'inline-block', minWidth: '14vw', fontWeight: 600 },
  topPod:   { display: 'inline-block', minWidth: '10vw', color: c.onNavyMuted },
  pairHint: { fontSize: '1.8vw', textAlign: 'center', lineHeight: 1.5, color: c.onNavyMuted },
  code:     { fontSize: '9vw', fontWeight: 800, letterSpacing: '0.2em', fontFamily: 'ui-monospace, Menlo, monospace' },
  retry:    { fontSize: '1.4vw', padding: '1vh 2vw', borderRadius: '8px', border: 'none', cursor: 'pointer' },
};
